import { useEffect, useMemo, useState } from "react"

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom"

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types"

// @mui material components
import List from "@mui/material/List"
import Divider from "@mui/material/Divider"
import Link from "@mui/material/Link"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React example components
import SidenavCollapse from "monitor/Sidenav/SidenavCollapse"
import config from "config"
// Custom styles for the Sidenav
import SidenavRoot from "monitor/Sidenav/SidenavRoot"

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context"

function Sidenav({ color, brand, brandName, version_, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller
  const { pathname } = useLocation()
  const collapseName = pathname.replace(/^\/+/, "")

  const isRouteActive = (itemKey, itemRoute) => {
    const normalizedRoute = (itemRoute || "").replace(/^\/+/, "")
    if (itemKey === collapseName) return true
    if (!normalizedRoute) return false
    return collapseName === normalizedRoute || collapseName.startsWith(`${normalizedRoute}/`)
  }

  // --- Estado de secciones abiertas/cerradas (por cada "title") ---
  const initialOpenSections = useMemo(() => {
    const state = {}
    routes.forEach((r) => {
      if (r.type === "title") {
        const k = r.title || r.name
        if (k) state[k] = true // abiertas por defecto
      }
    })
    return state
  }, [routes])

  const [openSections, setOpenSections] = useState(initialOpenSections)
  useEffect(() => {
    // Sincroniza si cambia la lista de títulos
    setOpenSections((prev) => {
      const next = { ...prev }
      Object.keys(initialOpenSections).forEach((k) => {
        if (!(k in next)) next[k] = true
      })
      Object.keys(next).forEach((k) => {
        if (!(k in initialOpenSections)) delete next[k]
      })
      return next
    })
  }, [initialOpenSections])

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))

  let textColor = "white"

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark"
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit"
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true)

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      const shouldBeMini = window.innerWidth < 1200

      setMiniSidenav(dispatch, shouldBeMini)

      if (shouldBeMini) {
        setTransparentSidenav(dispatch, false)
        setWhiteSidenav(dispatch, false)
      }
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav)

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav()

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav)
  }, [dispatch, pathname])

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = (() => {
    const out = []
    let currentSection = null

    routes.forEach((r, idx) => {
      const { type, name, icon, title, noCollapse, key, href, route } = r

      if (type === "title") {
        const k = title || name || `section-${idx}`
        currentSection = k
        out.push(
          <MDBox
            key={`title-${k}`}
            onClick={() => toggleSection(k)}
            px={3}
            mt={2}
            mb={1}
            ml={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            <MDTypography
              color={textColor}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
            >
              <Icon
                fontSize="small"
                sx={{
                  verticalAlign: "bottom",
                  transition: "transform 200ms",
                  transform: openSections?.[k] ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              >
                expand_more
              </Icon>{" "}
              {k}
            </MDTypography>
          </MDBox>
        )
        return
      }

      if (type === "divider") {
        out.push(
          <Divider
            key={`divider-${idx}`}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        )
        return
      }

      if (type === "collapse") {
        // Si hay sección vigente y está colapsada, no renderizamos este item
        if (currentSection && openSections && openSections[currentSection] === false) return

        if (href) {
          out.push(
            <Link
              href={href}
              key={key || `href-${idx}`}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={isRouteActive(key, route)}
                noCollapse={noCollapse}
              />
            </Link>
          )
        } else {
          out.push(
            <NavLink key={key || `route-${idx}`} to={route}>
              <SidenavCollapse name={name} icon={icon} active={isRouteActive(key, route)} />
            </NavLink>
          )
        }
      }
    })

    return out
  })()

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/dashboard" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="6rem" />}
          <MDBox width={"100%"}>
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
              sx={{ textTransform: "uppercase" }}
            >
              {brandName}
            </MDTypography>
            <MDTypography
              component="p"
              variant="caption"
              fontWeight="bold"
              color={textColor}
              sx={{ mt: 1 }}
              opacity={2}
            >
              {config.versionName}
            </MDTypography>
            <MDTypography
              component="p"
              variant="caption"
              fontWeight="light"
              color={textColor}
              sx={{ mt: 1 }}
              opacity={2}
            >
              v{version_}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  )
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  version_: PropTypes.string.isRequired,
}

export default Sidenav
